import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Box, Typography, TextField, Button, CircularProgress, Chip, Stack, IconButton,
} from '@mui/material';
import StarsIcon from '@mui/icons-material/Stars';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { useApplyLoyaltyMutation, useRemoveLoyaltyMutation } from '../../../services/orderApi';
import { useEnrollLoyaltyMutation } from '../../../services/customerApi';
import { setCurrentOrder } from '../../../store/orderSlice';
import { useToast } from '../../common/ToastProvider';
import CustomerWalletOtpModal from './CustomerWalletOtpModal';

const fmt = (v) =>
  `₹${parseFloat(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPts = (v) => Math.round(parseFloat(v || 0)).toLocaleString('en-IN');
const fmtWhen = (iso) =>
  new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

// apply_loyalty returns the raw code in error/details[] — map to cashier copy.
const errorCopy = (code, loyalty) => {
  switch (code) {
    case 'below_minimum':       return `Minimum ${fmtPts(loyalty?.min_redemption_points)} points needed to redeem.`;
    case 'exceeds_cap':         return 'Exceeds the max redeemable for this bill. Try a lower amount or tap Max.';
    case 'insufficient_points': return `Only ${fmtPts(loyalty?.points_balance)} points available.`;
    case 'not_enrolled':        return "Customer isn't enrolled in loyalty.";
    case 'no_customer':         return 'Add a customer to use loyalty.';
    case 'loyalty_frozen':      return 'This customer is excluded from loyalty — their points are frozen.';
    case 'redemption_cooldown': return loyalty?.redemption_available_at
      ? `Points were redeemed recently. Can redeem again after ${fmtWhen(loyalty.redemption_available_at)}.`
      : 'Points were redeemed recently. Try again later.';
    default:                    return 'Couldn’t apply loyalty. Try again.';
  }
};

// Tier badge — bg tinted from the tier's color_hex; ▲ when it earns a multiplier.
const TierChip = ({ tier }) => {
  if (!tier?.name) return null;
  const hex = tier.color_hex || '#94A3B8';
  return (
    <Chip
      size="small"
      label={`${tier.name}${parseFloat(tier.earn_multiplier || 1) > 1 ? ' ▲' : ''}`}
      sx={{
        height: 20, fontSize: '0.62rem', fontWeight: 800, borderRadius: '6px',
        bgcolor: `${hex}26`, color: '#1E293B', border: `1px solid ${hex}59`,
      }}
    />
  );
};

export default function CheckoutLoyaltyPanel({
  loyalty, customerId, isLoadingCustomer, onClose,
  walletOtpEnabled, walletOtpMode, wallet, otherWalletVerified,
}) {
  const dispatch  = useDispatch();
  const showToast = useToast();

  const { currentOrderId, grand_total, loyalty_redemption_amount, loyalty_points_redeemed } =
    useSelector((s) => s.order);

  const [points, setPoints] = useState('');
  const [error, setError]   = useState('');

  const [applyLoyalty,  { isLoading: isApplying }]  = useApplyLoyaltyMutation();
  const [removeLoyalty, { isLoading: isRemoving }]  = useRemoveLoyaltyMutation();
  const [enrollLoyalty, { isLoading: isEnrolling }] = useEnrollLoyaltyMutation();

  const rate    = parseFloat(loyalty?.redemption_rate || 0);
  const balance = parseFloat(loyalty?.points_balance || 0);
  const minPts  = parseFloat(loyalty?.min_redemption_points || 0);
  const maxPct  = parseFloat(loyalty?.max_redemption_pct || 100);
  const grand   = parseFloat(grand_total || 0);

  const appliedAmount = parseFloat(loyalty_redemption_amount || 0);
  const isApplied     = appliedAmount > 0;

  // Exact max the backend allows: floor(min(balance, grand * pct/100 * rate)).
  const maxPoints = useMemo(() => {
    if (!rate) return 0;
    return Math.floor(Math.min(balance, grand * (maxPct / 100) * rate));
  }, [rate, balance, grand, maxPct]);

  const enteredPoints = parseInt(points, 10) || 0;
  const previewAmount = rate ? enteredPoints / rate : 0;
  const maxAmount     = rate ? maxPoints / rate : 0;

  const handlePointsChange = (e) => {
    const v = e.target.value;
    if (!/^\d*$/.test(v)) return;
    setError('');
    if (v === '') { setPoints(''); return; }
    setPoints(String(Math.min(parseInt(v, 10), maxPoints)));
  };

  const handleApply = useCallback(async (auto) => {
    if (!currentOrderId) return;
    setError('');
    try {
      const arg = auto
        ? { id: currentOrderId }
        : { id: currentOrderId, points_to_redeem: enteredPoints };
      const res = await applyLoyalty(arg).unwrap();
      dispatch(setCurrentOrder(res.data.order));
      setPoints('');
      showToast('Loyalty applied', 'success');
    } catch (err) {
      // loyalty_gate_blocked (benefit-stacking-policy): backend already returns
      // a complete, cashier-facing message naming the blocking instrument family
      // — relay verbatim rather than mapping through errorCopy's short codes.
      if (err?.data?.details?.code === 'loyalty_gate_blocked') {
        setError(err.data.error);
        return;
      }
      const code = err?.data?.details?.[0] || err?.data?.error || '';
      setError(errorCopy(code, loyalty));
    }
  }, [currentOrderId, enteredPoints, applyLoyalty, dispatch, showToast, loyalty]);

  // When wallet OTP is required, Apply/Apply Max first verifies the customer,
  // then this fires the *real* redemption once that verification lands —
  // pendingAutoRef carries which Apply variant (typed points vs. auto-max)
  // was requested across the wait for the customer to read out the code.
  const pendingAutoRef = useRef(null);
  useEffect(() => {
    if (wallet && wallet.status === 'verified' && pendingAutoRef.current !== null) {
      const auto = pendingAutoRef.current;
      pendingAutoRef.current = null;
      handleApply(auto);
    }
  }, [wallet?.status]);

  const handleApplyClick = (auto) => {
    if (!walletOtpEnabled || !wallet) { handleApply(auto); return; }
    if (wallet.status === 'verified') { handleApply(auto); return; }
    if (wallet.status === 'pending') { pendingAutoRef.current = auto; wallet.openModal(); return; }
    if (wallet.status === 'blocked_phone' || wallet.status === 'blocked_connector') return;
    if (walletOtpMode === 'combined' && otherWalletVerified) {
      pendingAutoRef.current = auto;
      wallet.markVerifiedInstantly();
      showToast('Already verified for this order — applied instantly, no second code needed', 'success');
      return;
    }
    pendingAutoRef.current = auto;
    wallet.start();
  };

  const handleRemove = useCallback(async () => {
    if (!currentOrderId) return;
    try {
      const res = await removeLoyalty(currentOrderId).unwrap();
      dispatch(setCurrentOrder(res.data.order));
      showToast('Loyalty removed', 'info');
    } catch (err) {
      showToast(err?.data?.error || 'Failed to remove loyalty', 'error');
    }
  }, [currentOrderId, removeLoyalty, dispatch, showToast]);

  const handleEnroll = useCallback(async () => {
    if (!customerId) return;
    try {
      await enrollLoyalty(customerId).unwrap();
      showToast('Enrolled in loyalty', 'success');
    } catch (err) {
      showToast(err?.data?.error || 'Failed to enroll', 'error');
    }
  }, [customerId, enrollLoyalty, showToast]);

  // ── Gating ───────────────────────────────────────────────────────────────
  if (isLoadingCustomer && !loyalty) {
    return (
      <Box sx={{ mb: 1.5, px: 1.25, py: 1, borderRadius: '10px', border: '1px solid #E2E8F0', bgcolor: '#F8FAFC', display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={13} sx={{ color: '#94A3B8' }} />
        <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8' }}>Loading loyalty…</Typography>
      </Box>
    );
  }
  if (!loyalty || loyalty.enabled === false) return null;

  const enrolled          = loyalty.enrolled !== false;
  const redemptionEnabled = loyalty.redemption_enabled !== false && rate > 0;
  const belowMinimum      = balance < minPts;
  // Settings › Loyalty misuse guards: excluded customers earn nothing; frozen
  // ones can't redeem either; a cooldown blocks redeeming until the given time.
  const frozen            = loyalty.frozen === true;
  const cooldownUntil     = loyalty.redemption_available_at && new Date(loyalty.redemption_available_at) > new Date()
    ? loyalty.redemption_available_at : null;

  return (
    <>
    <Box sx={{
      mb: 1.5, px: 1.25, py: 1, borderRadius: '10px',
      border: '1px solid', borderColor: isApplied ? '#FCD34D' : '#E2E8F0',
      bgcolor: isApplied ? '#FFFBEB' : '#F8FAFC',
    }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.65 }}>
        <Stack direction="row" spacing={0.6} alignItems="center">
          <StarsIcon sx={{ fontSize: 15, color: '#F59E0B' }} />
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#92400E', letterSpacing: '0.04em' }}>
            LOYALTY POINTS
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <TierChip tier={loyalty.tier} />
          {onClose && (
            <IconButton size="small" onClick={onClose} sx={{ p: 0.25, color: '#94A3B8', '&:hover': { color: '#92400E' } }}>
              <CloseIcon sx={{ fontSize: 15 }} />
            </IconButton>
          )}
        </Stack>
      </Stack>

      {/* Not enrolled */}
      {!enrolled ? (
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography sx={{ fontSize: '0.74rem', color: '#64748B' }}>Not enrolled in loyalty</Typography>
          <Button
            size="small" variant="outlined" disableElevation
            onClick={handleEnroll} disabled={isEnrolling}
            sx={{
              fontSize: '0.72rem', fontWeight: 700, textTransform: 'none', borderRadius: '7px',
              color: '#4F46E5', borderColor: '#C7D2FE', bgcolor: '#EEF2FF', flexShrink: 0,
              '&:hover': { borderColor: '#A5B4FC', bgcolor: '#E0E7FF' },
            }}
          >
            {isEnrolling ? <CircularProgress size={13} /> : 'Enroll'}
          </Button>
        </Stack>

      /* Earn-only — redemption disabled org-wide */
      ) : !redemptionEnabled ? (
        <>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#1E293B' }}>
            {fmtPts(balance)} pts
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', mt: 0.1 }}>
            Points can’t be redeemed here
          </Typography>
        </>

      /* Excluded customer with a frozen balance, or redemption cooldown */
      ) : !isApplied && (frozen || cooldownUntil) ? (
        <>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#1E293B' }}>
            {fmtPts(balance)} pts
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', color: '#B45309', mt: 0.1 }}>
            {frozen
              ? 'Excluded from loyalty — points are frozen'
              : `Redeemed recently — can redeem again after ${fmtWhen(cooldownUntil)}`}
          </Typography>
        </>

      /* Applied state */
      ) : isApplied ? (
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 900, color: '#16A34A' }}>
              −{fmt(appliedAmount)} applied
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: '#64748B', mt: 0.1 }}>
              {fmtPts(loyalty_points_redeemed)} pts redeemed
            </Typography>
          </Box>
          <Button
            size="small" disabled={isRemoving} onClick={handleRemove}
            startIcon={isRemoving ? <CircularProgress size={12} /> : <CloseIcon sx={{ fontSize: '14px !important' }} />}
            sx={{
              fontSize: '0.72rem', fontWeight: 700, textTransform: 'none', borderRadius: '6px',
              color: '#64748B', minWidth: 0, px: 1, py: 0.4, flexShrink: 0,
              '&:hover': { bgcolor: '#FEF3C7', color: '#92400E' },
            }}
          >
            Remove
          </Button>
        </Stack>

      /* Wallet OTP: customer verification in progress or blocked */
      ) : walletOtpEnabled && wallet?.status === 'sending' ? (
        <Stack direction="row" alignItems="center" spacing={1}>
          <CircularProgress size={13} sx={{ color: '#4F46E5' }} />
          <Typography sx={{ fontSize: '0.72rem', color: '#64748B' }}>Sending code to customer’s phone…</Typography>
        </Stack>

      ) : walletOtpEnabled && wallet?.status === 'pending' ? (
        <Box
          onClick={wallet.openModal}
          sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.75, borderRadius: '7px', border: '1px solid #4F46E5', bgcolor: '#EEF2FF', cursor: 'pointer' }}
        >
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#4F46E5' }}>Code sent — tap to enter</Typography>
        </Box>

      ) : walletOtpEnabled && wallet?.status === 'blocked_phone' ? (
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#DC2626' }}>
          Customer has no phone number on file — redemption unavailable.
        </Typography>

      ) : walletOtpEnabled && wallet?.status === 'blocked_connector' ? (
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#DC2626' }}>
          Wallet verification isn’t available (no connector configured).
        </Typography>

      /* Redeemable */
      ) : (
        <>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1E293B', mb: 0.75 }}>
            {fmtPts(balance)} pts · worth {fmt(loyalty.redeemable_amount)}
          </Typography>

          {belowMinimum ? (
            <Typography sx={{ fontSize: '0.7rem', color: '#92400E', fontWeight: 600 }}>
              Need {fmtPts(minPts)} points to redeem.
            </Typography>
          ) : loyalty.allow_partial ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} alignItems="stretch">
              <TextField
                size="small" placeholder="Points"
                value={points} onChange={handlePointsChange}
                error={Boolean(error)}
                inputProps={{ inputMode: 'numeric', 'aria-label': 'Points to redeem', style: { fontSize: '0.82rem', fontWeight: 700 } }}
                InputProps={{ sx: { borderRadius: '8px', height: 36, bgcolor: '#FFF' } }}
                sx={{ flex: 1 }}
              />
              <Stack direction="row" spacing={0.75}>
                <Button
                  variant="contained" disableElevation
                  onClick={() => handleApplyClick(false)}
                  disabled={isApplying || enteredPoints < minPts || enteredPoints <= 0}
                  sx={{
                    height: 36, minWidth: 64, borderRadius: '8px', fontWeight: 700, fontSize: '0.78rem',
                    textTransform: 'none', bgcolor: '#4F46E5', flexShrink: 0,
                    '&:hover': { bgcolor: '#4338CA' },
                    '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
                  }}
                >
                  {isApplying ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Apply'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handleApplyClick(true)}
                  disabled={isApplying || maxPoints < minPts}
                  sx={{
                    height: 36, minWidth: 52, borderRadius: '8px', fontWeight: 700, fontSize: '0.78rem',
                    textTransform: 'none', color: '#4F46E5', borderColor: '#C7D2FE', flexShrink: 0,
                    '&:hover': { borderColor: '#A5B4FC', bgcolor: '#EEF2FF' },
                  }}
                >
                  Max
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Button
              fullWidth variant="contained" disableElevation
              onClick={() => handleApplyClick(true)}
              disabled={isApplying || maxPoints < minPts}
              sx={{
                height: 38, borderRadius: '8px', fontWeight: 800, fontSize: '0.8rem',
                textTransform: 'none', bgcolor: '#4F46E5',
                '&:hover': { bgcolor: '#4338CA' },
                '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
              }}
            >
              {isApplying ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : `Apply Max −${fmt(maxAmount)}`}
            </Button>
          )}

          {/* Helper / error */}
          {error ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.6 }}>
              <WarningAmberIcon sx={{ fontSize: 13, color: '#DC2626', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.68rem', color: '#DC2626', fontWeight: 600 }}>{error}</Typography>
            </Box>
          ) : !belowMinimum && (
            <Typography sx={{ fontSize: '0.66rem', color: '#94A3B8', mt: 0.6 }}>
              {loyalty.allow_partial && enteredPoints > 0 ? `= ${fmt(previewAmount)} off · ` : ''}
              {minPts > 0 ? `min ${fmtPts(minPts)} · ` : ''}≤{Math.round(maxPct)}% of bill
            </Typography>
          )}
        </>
      )}
    </Box>

    {walletOtpEnabled && wallet && (
      <CustomerWalletOtpModal
        open={wallet.status === 'pending' && wallet.modalOpen}
        onClose={wallet.closeModal}
        title="Verify Customer — Loyalty Points"
        secondsLeft={wallet.secondsLeft}
        resendCooldown={wallet.resendCooldown}
        isResending={wallet.isResending}
        isVerifying={wallet.isVerifying}
        onVerify={wallet.verify}
        onResend={wallet.resend}
      />
    )}
    </>
  );
}
